import React, { useEffect, useState, useRef } from "react"

import styled from "styled-components"

import { spacing } from "@izettle/otto"
import { useLocalization } from "@izettle/tenko-react"

import { useModal } from "@/core/components/modal"
import {
  Container,
  Content,
  Flex,
  WallHighlight,
  Button,
  Title2,
  SmallSecondaryText,
  RegularText,
} from "@/core/components/shared"
import { useSlider } from "@/core/components/slider"
import { Phone } from "@/modules/sign-up/api/types"
import { useSendReceiptPost } from "@/modules/sign-up/api/use-send-receipt"
import ErrorDialog from "@/modules/sign-up/components/error-dialog"
import {
  TrackingAction,
  useSignUpTracking,
} from "@/modules/sign-up/hooks/tracking-sign-up"
import formatPhone from "@/modules/sign-up/shared/phone-format"

import { Modals, SlideIndexs } from "./types"

const ButtonGroup = styled(Flex)`
  margin-top: ${spacing.small};
  button:first-child {
    margin-right: ${spacing.small};
  }
`

const CenterContainer = styled.div`
  text-align: "center";
`

const Img = styled.img`
  height: 100%;
  max-width: 100%;
  margin-top: ${spacing.small};
  margin-bottom: ${spacing.medium};
`

export type SignUpSuccessProps = {
  customerUuid: string | null
  purchaseUuid: string
  orgName: string
  amount: string
  time: string
  email?: string
  phone?: Phone
}

export const SignUpSuccess = ({
  orgName,
  amount,
  time,
  phone,
  email,
  customerUuid,
  purchaseUuid,
}: SignUpSuccessProps) => {
  const ref = useRef(null)
  const { text } = useLocalization()
  const [showModal] = useModal(Modals.ContactOptions)
  const { goTo, index } = useSlider()
  const [dialogMessage, setDialogMessage] = useState("")
  const { track } = useSignUpTracking(purchaseUuid)

  useEffect(() => {
    if (index === SlideIndexs.SignUpSuccess) {
      track(TrackingAction.ViewedSignUpComplete)
    }
  }, [index])

  const closeDialog = () => {
    setDialogMessage("")
  }

  const { mutate, isLoading } = useSendReceiptPost({
    onSuccess: ({ failureMessage, phone: successPhone, email: successEmail }) => {
      if (!failureMessage) {
        if (successPhone) {
          goTo(SlideIndexs.ContactSent, {
            phone: formatPhone(successPhone),
          })
        } else {
          goTo(SlideIndexs.ContactSent, { email: successEmail })
        }
      } else {
        setDialogMessage(`SIGNUP_ERROR_${failureMessage}`)
      }
    },
  })

  const sendReceipt = (key: string, value?: string | Phone) => {
    if (customerUuid && purchaseUuid) {
      mutate({
        customerUuid,
        purchaseUuid,
        contactInfo: {
          [key]: value,
        },
      })
    } else {
      // [TODO] replace with proper error message if needed
      // eslint-disable-next-line no-console
      console.error("Missing customer", customerUuid)
    }
  }

  const handleNoReceipt = () => {
    goTo(SlideIndexs.ThankYou)
    track(TrackingAction.ClickedNoReceipt)
  }
  const handleSendReceipt = () => {
    if (phone && !email) {
      sendReceipt("phone", phone)
    } else if (email && !phone) {
      sendReceipt("email", email)
    } else {
      showModal(ref)
    }
    track(TrackingAction.ClickedSendReceipt)
  }

  return (
    <Container withNoHeaderSpace>
      <Content>
        <WallHighlight>
          <Title2>{text("SIGNUP_SIGNUP_ALL_DONE")}</Title2>
          <RegularText>{text("SIGNUP_WE_SENT_YOU_A_WELCOME_EMAIL")}</RegularText>
          <Img
            src="https://cdn.izettle.com/customers/loyal-customer.svg"
            alt="success"
            aria-hidden
          />
        </WallHighlight>
        <CenterContainer>
          <Title2>{text("SIGNUP_DO_YOU_WANT_YOUR_RECEIPT")}</Title2>
          <SmallSecondaryText>
            {text("SIGNUP_FOR_YOUR_PURCHASED_AMOUNT_AT_TIME_AT_MERCHANT", [
              amount,
              time,
              orgName,
            ])}
          </SmallSecondaryText>
        </CenterContainer>
        <ButtonGroup>
          <Button fullWidth onClick={handleNoReceipt} variant="secondary">
            {text("SIGNUP_NO")}
          </Button>
          <Button fullWidth onClick={handleSendReceipt} disabled={isLoading} ref={ref}>
            {text("SIGNUP_SEND_RECEIPT")}
          </Button>
        </ButtonGroup>
      </Content>
      <ErrorDialog onClose={closeDialog} message={dialogMessage} />
    </Container>
  )
}
